<template>
  <component
    :is="hasLabelContent ? 'label' : 'div'"
    class="radio-button__label"
    :class="{ 'radio-button__label--disabled': disabled }"
  >
    <div class="radio-button__label-content" v-if="hasLabelContent">
      <component v-if="icon" :is="iconComponent" class="radio-button__label-icon" />

      <slot />
    </div>

    <input
      class="radio-button__input"
      :class="`radio-button__input--position-${position}`"
      type="radio"
      ref="input"
      :name="name"
      :disabled="disabled"
      :value="modelValue"
      :checked="checked"
      @change="$emit('update:modelValue', $event.target.value)"
      @click="onClick"
    />
  </component>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, Component, PropType } from 'vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'RadioButton',

  emits: {
    'update:modelValue': null,
    click: null,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      required: true,
      type: [Boolean, String],
    },

    model: {
      required: false,
      type: [Boolean, String],
      default: null,
    },

    icon: {
      type: String,
      default: null,
    },

    /**
     * end: align toggle at the end of container
     * start: align toggle at the start of container
     */
    position: {
      type: String as PropType<'end' | 'start'>,
      default: 'end',
    },

    name: {
      type: String,
      default: null,
    },
  },

  computed: {
    checked(): boolean {
      return this.modelValue === this.model;
    },

    // dynamically register the icon component to avoid having to include here
    // all the icon components that are used
    iconComponent(): () => Promise<Component> {
      return defineAsyncComponent(() => import(`@/components/Icons/${this.icon}.vue`));
    },

    hasLabelContent(): boolean {
      return Boolean(this.icon) || hasSlotContent(this.$slots.default, {});
    },
  },

  methods: {
    onClick($event: MouseEvent) {
      this.$emit('click', $event);

      // radio inputs don't emit "change" when you uncheck them,
      // so listen to click (and by extension space key press) and emit change if
      // currently checked
      if (!this.disabled && this.checked) {
        this.$emit('update:modelValue', $event, this.modelValue);
      }
    },

    /**
     * To focus the radio from outside the component
     *
     * @public
     */
    focus() {
      (this.$refs.input as HTMLInputElement).focus();
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
.radio-button__label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;

  &:not(.disabled) {
    cursor: pointer;
  }
}

.radio-button__label-content {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;

  svg {
    flex-shrink: 0;
  }
}

.radio-button__input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border-radius: 50%;
  background: colors.$color-white;
  min-width: 18px;
  height: 18px;
  box-shadow: settings.$box-shadow-border-grey-50, settings.$box-shadow-layer-1;
  outline: none;
  transition: all 0.2s ease-in-out;

  &--position-start {
    order: -1;
  }

  &:not(:disabled) {
    cursor: pointer;

    &:active,
    &:hover {
      box-shadow: settings.$box-shadow-border-blue, settings.$box-shadow-layer-1;
    }

    &:checked {
      box-shadow: inset 0 0 0 6px colors.$color-blue, settings.$box-shadow-layer-1;
    }
  }

  &:disabled {
    background: colors.$color-grey-50;
    box-shadow: settings.$box-shadow-border-grey-50;

    &:checked {
      background: colors.$color-white;
      box-shadow: inset 0 0 0 6px colors.$color-blue-300;
    }
  }
}
</style>
